import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Button,
  LinearProgress,
} from '@material-ui/core';
import {
  FlashOn,
  TrendingUp,
  Warning,
  ErrorOutline,
  InfoOutlined,
  CheckCircleOutline,
  ArrowForward,
} from '@material-ui/icons';
import { useHistory } from 'react-router-dom';
import { formattedCurrency } from '../../../utils/utils';

const SEVERITY_CONFIG = {
  success: {
    bg: '#f0fdf4',
    border: '#388e3c',
    text: '#1b5e20',
    iconColor: '#388e3c',
    icon: <CheckCircleOutline fontSize="small" />,
  },
  warning: {
    bg: '#fffbf0',
    border: '#dd6b20',
    text: '#c05621',
    iconColor: '#dd6b20',
    icon: <Warning fontSize="small" />,
  },
  error: {
    bg: '#fff5f5',
    border: '#e53e3e',
    text: '#c53030',
    iconColor: '#e53e3e',
    icon: <ErrorOutline fontSize="small" />,
  },
  info: {
    bg: '#f0f7ff',
    border: '#1976d2',
    text: '#1565c0',
    iconColor: '#1976d2',
    icon: <InfoOutlined fontSize="small" />,
  },
};

const TodayPulseBanner = ({ todayPulse }) => {
  const history = useHistory();
  if (!todayPulse) return null;

  const {
    salesTotal = 0,
    salesCount = 0,
    receivedTotal = 0,
    averageDailyBaseline = 0,
    pacePercentage = 0,
    openSalesCount = 0,
    openSalesAmount = 0,
    insights = [],
  } = todayPulse;

  const clampedProgress = Math.min(100, Math.max(0, Number(pacePercentage || 0)));

  return (
    <Box
      style={{
        padding: '16px 20px',
        borderRadius: 8,
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
      }}
    >
      <Grid container spacing={3} alignItems="stretch">
        {/* Coluna Esquerda: Resumo Ao Vivo de Hoje */}
        <Grid item xs={12} md={5}>
          <Box
            style={{
              padding: '16px 18px',
              backgroundColor: '#ffffff',
              borderLeft: '4px solid #1976d2',
              borderRadius: 6,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Box display="flex" alignItems="center" mb={1}>
              <Box
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                style={{
                  backgroundColor: '#e3f2fd',
                  color: '#1976d2',
                  borderRadius: 6,
                  padding: 4,
                  marginRight: 8,
                }}
              >
                <FlashOn fontSize="small" />
              </Box>
              <Typography
                variant="overline"
                style={{
                  color: '#555',
                  fontWeight: 700,
                  letterSpacing: '0.8px',
                  lineHeight: 1.2,
                }}
              >
                PULSO DE HOJE • AO VIVO
              </Typography>
            </Box>

            <Box display="flex" alignItems="baseline" flexWrap="wrap" mb={1}>
              <Typography
                variant="h4"
                style={{ fontWeight: 800, color: '#1976d2', marginRight: 12 }}
              >
                {formattedCurrency(salesTotal)}
              </Typography>
              <Chip
                size="small"
                label={`${salesCount} ${salesCount === 1 ? 'venda hoje' : 'vendas hoje'}`}
                style={{
                  backgroundColor: '#e3f2fd',
                  color: '#1976d2',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                }}
              />
            </Box>

            <Box display="flex" flexWrap="wrap" style={{ gap: 16, marginBottom: 12 }}>
              <Typography variant="body2" style={{ color: '#555' }}>
                Recebido hoje:{' '}
                <strong style={{ color: '#388e3c' }}>
                  {formattedCurrency(receivedTotal)}
                </strong>
              </Typography>
              {openSalesCount > 0 && (
                <Typography variant="body2" style={{ color: '#555' }}>
                  A receber de hoje:{' '}
                  <strong style={{ color: '#f57c00' }}>
                    {formattedCurrency(openSalesAmount)} ({openSalesCount})
                  </strong>
                </Typography>
              )}
            </Box>

            {averageDailyBaseline > 0 && (
              <Box>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="caption" style={{ color: '#666', fontWeight: 500 }}>
                    Ritmo vs. Média Diária ({formattedCurrency(averageDailyBaseline)})
                  </Typography>
                  <Typography
                    variant="caption"
                    style={{
                      fontWeight: 700,
                      color: pacePercentage >= 100 ? '#388e3c' : '#1976d2',
                    }}
                  >
                    {pacePercentage.toFixed(0)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={clampedProgress}
                  color="primary"
                  style={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: '#e3f2fd',
                  }}
                />
              </Box>
            )}
          </Box>
        </Grid>

        {/* Coluna Direita: Insights Operacionais do Dia */}
        <Grid item xs={12} md={7}>
          <Box mb={1} display="flex" alignItems="center">
            <TrendingUp
              fontSize="small"
              style={{ color: '#1976d2', marginRight: 6 }}
            />
            <Typography
              variant="subtitle2"
              style={{ color: '#1D3B44', fontWeight: 700 }}
            >
              Principais Insights & Alertas do Dia
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {insights.map((ins) => {
              const cfg = SEVERITY_CONFIG[ins.severity] || SEVERITY_CONFIG.info;
              return (
                <Grid item xs={12} sm={6} key={ins.id}>
                  <Box
                    p={1.5}
                    style={{
                      backgroundColor: cfg.bg,
                      borderLeft: `4px solid ${cfg.border}`,
                      borderRadius: '0 8px 8px 0',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box>
                      <Box display="flex" alignItems="center" mb={0.5}>
                        <Box
                          component="span"
                          display="inline-flex"
                          mr={0.75}
                          style={{ color: cfg.iconColor }}
                        >
                          {cfg.icon}
                        </Box>
                        <Typography
                          variant="subtitle2"
                          style={{
                            fontWeight: 700,
                            fontSize: '0.84rem',
                            color: cfg.text,
                          }}
                        >
                          {ins.title}
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        display="block"
                        style={{
                          color: '#4a5568',
                          lineHeight: 1.4,
                        }}
                      >
                        {ins.message}
                      </Typography>
                    </Box>

                    {ins.route && (
                      <Box mt={1} display="flex" justifyContent="flex-end">
                        <Button
                          size="small"
                          onClick={() => history.push(ins.route)}
                          endIcon={<ArrowForward style={{ fontSize: 14 }} />}
                          style={{
                            color: cfg.text,
                            fontWeight: 700,
                            fontSize: '0.72rem',
                            padding: '2px 8px',
                            textTransform: 'none',
                          }}
                        >
                          {ins.actionText || 'Abrir'}
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TodayPulseBanner;
